import { useMutation, useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { useAuth } from '@/features/auth/AuthContext';
import { createPasswordReset, listMembers, type PasswordResetCreated } from '@/lib/api';

interface Props {
  open: boolean;
  onClose: () => void;
}

/** Owner gera link de uso único (30 min) pra um membro redefinir a senha. */
export function ResetLinkModal({ open, onClose }: Props) {
  const { me } = useAuth();
  const householdId = me?.household?.id ?? '';
  const membersQ = useQuery({
    queryKey: ['members', householdId],
    queryFn: () => listMembers(householdId),
    enabled: open && householdId !== '',
  });

  // fechar clicando fora só se o clique começou e terminou no backdrop
  const downOnBackdrop = useRef(false);
  const [userId, setUserId] = useState('');
  const [created, setCreated] = useState<PasswordResetCreated | null>(null);
  const [copied, setCopied] = useState(false);

  const mutation = useMutation({
    mutationFn: createPasswordReset,
    onSuccess: (res) => setCreated(res),
  });

  if (!open) return null;

  function close() {
    setUserId('');
    setCreated(null);
    setCopied(false);
    mutation.reset();
    onClose();
  }

  async function copy() {
    if (!created) return;
    await navigator.clipboard.writeText(created.reset_url);
    setCopied(true);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={(e) => {
        downOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (downOnBackdrop.current && e.target === e.currentTarget) close();
        downOnBackdrop.current = false;
      }}
    >
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-xl">
        <h2 className="mb-1 text-lg font-semibold">Redefinir senha de membro</h2>
        <p className="mb-4 text-sm text-slate-500">
          Gera um link de uso único, válido por 30 minutos. Envie por um canal seguro (WhatsApp).
        </p>

        {!created && (
          <div className="flex flex-col gap-3 text-sm">
            <label className="flex flex-col gap-1">
              <span className="text-slate-600">Membro</span>
              <select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="rounded-md border border-slate-300 px-3 py-2 outline-none focus:border-slate-900"
              >
                <option value="">— selecionar —</option>
                {membersQ.data?.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.user.full_name} ({m.user.email})
                  </option>
                ))}
              </select>
            </label>
            {mutation.isError && (
              <p role="alert" className="text-red-600">
                {(mutation.error as Error).message}
              </p>
            )}
            <div className="mt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={close}
                className="rounded-md border border-slate-300 px-3 py-2 hover:bg-slate-100"
              >
                cancelar
              </button>
              <button
                type="button"
                disabled={!userId || mutation.isPending}
                onClick={() => mutation.mutate(userId)}
                className="rounded-md bg-slate-900 px-3 py-2 font-medium text-white disabled:opacity-50"
              >
                {mutation.isPending ? 'gerando…' : 'gerar link'}
              </button>
            </div>
          </div>
        )}

        {created && (
          <div className="flex flex-col gap-3 text-sm">
            <p className="break-all rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs">
              {created.reset_url}
            </p>
            <p className="text-slate-500">
              Válido até {new Date(created.expires_at).toLocaleTimeString('pt-BR')} — uso único.
            </p>
            <div className="mt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={copy}
                className="rounded-md bg-slate-900 px-3 py-2 font-medium text-white"
              >
                {copied ? 'copiado ✓' : 'copiar link'}
              </button>
              <button
                type="button"
                onClick={close}
                className="rounded-md border border-slate-300 px-3 py-2 hover:bg-slate-100"
              >
                fechar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
